/**
 * CompanySubscriptionContext
 *
 * Provides company subscription state and grace period logic for the company portal.
 * Frontend checks are for UX only - actual security is enforced via RLS policies.
 *
 * @see docs/features/company-subscription-grace-periods-todo.md
 * @see docs/architecture/subscription-database-schema.md
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from '@xanectech/auth';
import { supabase } from '@xanectech/supabase-client';
import type { Database } from '@xanectech/supabase-client';

import { createLogger } from '@xanectech/lib';
import {
  CompanySubscription,
  CompanySubscriptionContextType,
  CompanySubscriptionState,
  GracePeriodInfo,
  GracePeriodReason,
} from '@xanectech/types';

const logger = createLogger('Data - CompanySubscriptionContext');

// ============================================
// Types
// ============================================

type CompanyTier = Database['public']['Enums']['company_tier'];
type SubscriptionStatus = Database['public']['Enums']['subscription_status'];

// ============================================
// Constants
// ============================================

const TIER_HIERARCHY: Record<CompanyTier, number> = {
  starter: 0,
  professional: 1,
  business: 2,
  enterprise: 3,
  custom: 4,
};

const DEFAULT_CANCELLATION_GRACE_DAYS = 30;
const DEFAULT_NON_PAYMENT_GRACE_DAYS = 14;

// ============================================
// Context
// ============================================

const CompanySubscriptionContext = createContext<
  CompanySubscriptionContextType | undefined
>(undefined);

// ============================================
// Provider
// ============================================

interface CompanySubscriptionProviderProps {
  children: ReactNode;
}

export function CompanySubscriptionProvider({
  children,
}: CompanySubscriptionProviderProps) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<CompanySubscription | null>(
    null,
  );
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [gracePeriodDays, setGracePeriodDays] = useState({
    cancellation: DEFAULT_CANCELLATION_GRACE_DAYS,
    nonPayment: DEFAULT_NON_PAYMENT_GRACE_DAYS,
  });

  /**
   * Fetch grace period parameters from database
   */
  const fetchGracePeriodParams = useCallback(async () => {
    try {
      const { data: cancellationParam } = await supabase
        .from('parameters')
        .select('value')
        .eq('key', 'company_cancellation_grace_period')
        .eq('is_current', true)
        .maybeSingle();

      const { data: nonPaymentParam } = await supabase
        .from('parameters')
        .select('value')
        .eq('key', 'company_non_payment_grace_period')
        .eq('is_current', true)
        .maybeSingle();

      setGracePeriodDays({
        cancellation: cancellationParam?.value
          ? parseInt(String(cancellationParam.value), 10)
          : DEFAULT_CANCELLATION_GRACE_DAYS,
        nonPayment: nonPaymentParam?.value
          ? parseInt(String(nonPaymentParam.value), 10)
          : DEFAULT_NON_PAYMENT_GRACE_DAYS,
      });
    } catch (err) {
      logger.warn('Failed to fetch grace period parameters, using defaults:', {
        error: String(err),
      });
    }
  }, []);

  /**
   * Fetch user's company and subscription
   */
  const fetchSubscription = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      setCompanyId(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First, get the user's company (from user_profiles or company_users table)
      // For now, we'll check if user is a company admin
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('id')
        .eq('primary_admin_id', user.id)
        .maybeSingle();

      if (companyError && companyError.code !== 'PGRST116') {
        throw companyError;
      }

      if (!companyData) {
        // User is not a company admin - check if they're a company user
        // For now, we'll just set no subscription
        setSubscription(null);
        setCompanyId(null);
        setIsLoading(false);
        return;
      }

      setCompanyId(String(companyData.id));

      // Fetch company subscription
      const { data: subData, error: subError } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', String(companyData.id))
        .maybeSingle();

      if (subError && subError.code !== 'PGRST116') {
        throw subError;
      }

      if (subData) {
        const companySub: CompanySubscription = {
          id: subData.id,
          company_id: subData.company_id,
          tier: subData.tier,
          status: subData.status,
          billing_type: subData.billing_type as 'per_client' | 'fixed_yearly',
          estimated_clients: subData.estimated_clients,
          actual_clients: subData.actual_clients,
          total_users: subData.total_users,
          active_users: subData.active_users,
          current_period_start: subData.current_period_start,
          current_period_end: subData.current_period_end,
          cancel_at_period_end: subData.cancel_at_period_end ?? false,
          canceled_at: subData.canceled_at,
          stripe_customer_id: subData.stripe_customer_id,
          stripe_subscription_id: subData.stripe_subscription_id,
          fixed_yearly_amount: subData.fixed_yearly_amount,
          prepaid_years: subData.prepaid_years,
          discount_applied: subData.discount_applied
            ? Number(subData.discount_applied)
            : null,
          created_at: subData.created_at,
          updated_at: subData.updated_at,
        };
        setSubscription(companySub);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      logger.error('Error fetching company subscription:', err as Error);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch subscription'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Fetch data on mount and when user changes
  useEffect(() => {
    void fetchGracePeriodParams();
    void fetchSubscription();
  }, [fetchGracePeriodParams, fetchSubscription]);

  /**
   * Calculate grace period information
   */
  const gracePeriod = useMemo((): GracePeriodInfo => {
    if (!subscription) {
      return {
        isInGracePeriod: false,
        reason: null,
        daysRemaining: 0,
        expiresAt: null,
        gracePeriodDays: 0,
      };
    }

    const now = new Date();
    let reason: GracePeriodReason = null;
    let graceStartDate: Date | null = null;
    let graceDays = 0;

    // Check for non-payment (past_due status)
    if (subscription.status === 'past_due') {
      reason = 'non_payment';
      graceDays = gracePeriodDays.nonPayment;
      // Grace period starts from when payment became past due
      // Using current_period_end as the reference
      if (subscription.current_period_end) {
        graceStartDate = new Date(subscription.current_period_end);
      }
    }
    // Check for cancellation
    else if (subscription.status === 'canceled' || subscription.canceled_at) {
      reason = 'cancellation';
      graceDays = gracePeriodDays.cancellation;
      // Grace period starts from cancellation date or period end
      if (subscription.canceled_at) {
        graceStartDate = new Date(subscription.canceled_at);
      } else if (subscription.current_period_end) {
        graceStartDate = new Date(subscription.current_period_end);
      }
    }

    if (!reason || !graceStartDate) {
      return {
        isInGracePeriod: false,
        reason: null,
        daysRemaining: 0,
        expiresAt: null,
        gracePeriodDays: 0,
      };
    }

    const expiresAt = new Date(graceStartDate);
    expiresAt.setDate(expiresAt.getDate() + graceDays);

    const daysRemaining = Math.max(
      0,
      Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
    );
    const isInGracePeriod = now < expiresAt;

    return {
      isInGracePeriod,
      reason,
      daysRemaining,
      expiresAt,
      gracePeriodDays: graceDays,
    };
  }, [subscription, gracePeriodDays]);

  /**
   * Calculate overall subscription state
   */
  const subscriptionState = useMemo((): CompanySubscriptionState => {
    // Companies without subscriptions are treated as locked out
    if (!subscription) {
      return 'locked_out';
    }

    switch (subscription.status) {
      case 'active':
        return 'active';
      case 'past_due':
      case 'canceled':
        return gracePeriod.isInGracePeriod ? 'in_grace_period' : 'locked_out';
      case 'paused':
        return 'in_grace_period';
      case 'free':
      default:
        return 'locked_out';
    }
  }, [subscription, gracePeriod]);

  /**
   * Check if company has access at or above a specific tier
   */
  const hasTierAccess = useCallback(
    (minTier: CompanyTier): boolean => {
      if (!subscription) {
        return minTier === 'starter';
      }

      if (subscriptionState === 'locked_out') {
        return false;
      }

      return TIER_HIERARCHY[subscription.tier] >= TIER_HIERARCHY[minTier];
    },
    [subscription, subscriptionState],
  );

  /**
   * Refresh subscription data
   */
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  const value = useMemo<CompanySubscriptionContextType>(
    () => ({
      subscription,
      companyId,
      subscriptionState,
      gracePeriod,
      isLoading,
      error,
      hasTierAccess,
      refreshSubscription,
    }),
    [
      subscription,
      companyId,
      subscriptionState,
      gracePeriod,
      isLoading,
      error,
      hasTierAccess,
      refreshSubscription,
    ],
  );

  return (
    <CompanySubscriptionContext.Provider value={value}>
      {children}
    </CompanySubscriptionContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

/**
 * Hook to access company subscription context
 * Must be used within a CompanySubscriptionProvider
 */
export function useCompanySubscription(): CompanySubscriptionContextType {
  const context = useContext(CompanySubscriptionContext);
  if (context === undefined) {
    throw new Error(
      'useCompanySubscription must be used within a CompanySubscriptionProvider',
    );
  }
  return context;
}
